import * as React from 'react';
import { METHODS } from 'api/client';
import { useIntl } from 'react-intl';
import { Message } from 'i18n';
import { useNotifications, useFetch, useRouter } from 'hooks';
import { OnboardingAction, useOnboarding } from 'context/onboardingContext';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { clientSellerSchema } from 'utils/validation.utils';
import { size } from 'utils/lodash.utils';
import type { ClientInformationType } from 'types/common.types';
import { ClientRouter } from 'router/routes';
import { getRoute } from 'utils/sidebar.utils';
import { removeEmptyValues } from 'utils/common.utils';

const useSellerInformation = () => {
  const intl = useIntl();
  const methods = useForm<ClientInformationType>({
    resolver: yupResolver(clientSellerSchema),
    defaultValues: { firstName: '', lastName: '', email: '' },
    mode: 'all',
    shouldUnregister: false,
    reValidateMode: 'onChange',
  });
  const { reset, control, setValue, formState, setError } = methods;
  const email = useWatch({ control, name: 'email' });
  const { errors } = formState;
  const isPassword = React.useMemo(() => Boolean(email) && !errors.email, [
    email,
    errors.email,
  ]);
  const { history, location } = useRouter();
  const [storePass, setStorePass] = React.useState('');
  const { showNotification } = useNotifications();
  const [{ selectedFloor, image, newPropertyType }, dispatch] = useOnboarding();
  const onUpdatePassword = React.useCallback(() => {
    let generatedPassword: string = '0';
    const newVal = (Math.random() + 1)
      .toString(36)
      .slice(0, 10)
      .toUpperCase()
      .split('.');
    generatedPassword = newVal && newVal[1];
    setStorePass(generatedPassword);
    setValue('password', generatedPassword, {
      shouldValidate: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const onSuccess = React.useCallback(() => {
    const html = document.querySelector('html') as HTMLHtmlElement;
    html.style.overflowY = '';
    dispatch({ type: OnboardingAction.SET_IMAGE, payload: null });
    if (location.pathname.includes('/app/properties'))
      history.push(ClientRouter.PROPERTIES);
    else if (location.pathname.includes('/app/landing'))
      history.push(getRoute([location.pathname, '/thankyou']));
    else
      showNotification({
        key: 'user-add-success',
        message: 'User added successfully',
        severity: 'success',
      });
  }, [history, location.pathname, showNotification, dispatch]);

  const onFailure = React.useCallback(
    (error: string) => {
      if (typeof error === 'string') {
        const isEmail: boolean = JSON.parse(error)?.user?.email?.[0] ?? false;
        const isPhone: boolean =
          JSON.parse(error)?.user?.phone_number?.[0] ?? false;
        const isFirstName: boolean =
          JSON.parse(error)?.user?.first_name?.[0] ?? false;
        const isLastName: boolean =
          JSON.parse(error)?.user?.last_name?.[0] ?? false;

        if (isFirstName) {
          setError('firstName', {
            message: JSON.parse(error)?.user?.first_name?.[0],
          });
        }
        if (isLastName) {
          setError('lastName', {
            message: JSON.parse(error)?.user?.last_name?.[0],
          });
        }
        if (isEmail) {
          showNotification({
            key: 'email-error',
            message: intl.formatMessage({ id: Message.CLIENT_EMAIL_ERROR }),
            severity: 'error',
          });
          setError('email', {
            message: intl.formatMessage({ id: Message.CLIENT_EMAIL_ERROR }),
          });
        }
        if (isPhone) {
          setError('phone', {
            message: intl.formatMessage({ id: Message.CLIENT_PHONE_ERROR }),
          });
          showNotification({
            key: 'phone-error',
            message: intl.formatMessage({ id: Message.CLIENT_PHONE_ERROR }),
            severity: 'error',
          });
        }
      } else {
        showNotification({
          key: 'phone-error',
          message: error,
          severity: 'error',
        });
      }

      // LogsAPI.postUserLogs({
      //   area: userLogData.eventArea.registration,
      //   section: userLogData.eventSection.planUpload,
      //   name: userLogData.eventName.nextFailed,
      //   path: userLogData.eventPath.appOnBoardingUploadPlanPath,
      //   metadata: {
      //     failure_reason: isEmail
      //       ? intl.formatMessage({ id: Message.CLIENT_EMAIL_ERROR })
      //       : error,
      //   },
      // });
    },
    [intl, showNotification, setError]
  );

  const { isLoading, callFetch } = useFetch({
    initialUrl: '/api/properties/add_seller_property/',
    skipOnStart: true,
    config: {
      method: METHODS.POST,
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    },
    onSuccess,
    onFailure,
  });
  const handleSave = React.useCallback(
    (payload: ClientInformationType) => {
      const param = {
        first_name: payload.firstName,
        last_name: payload.lastName,
        phone_number: payload?.phone ? `+${payload?.phone}` : payload?.phone,
        ...(isPassword
          ? {
              email: payload.email,
              password: payload.password ? payload.password : storePass,
            }
          : {}),
      };
      if (newPropertyType === 'upload' && (!image || size(image.name) > 124)) {
        showNotification({
          key: 'uploadPlan/uploadPlanRejected',
          message:
            'File name has to be shorter than 124 characters, Please rename your file.',
          severity: 'error',
        });
      } else {
        const formData = new FormData();
        if (image) formData.append('file', image as Blob);
        formData.append('user', JSON.stringify(param));
        if (payload.supplier)
          formData.append('supplier', JSON.stringify(payload.supplier));
        if (selectedFloor)
          formData.append(
            'pre_made_property',
            JSON.stringify(selectedFloor.id)
          );
        callFetch(
          removeEmptyValues({
            data: formData,
          })
        );
      }
    },
    [
      isPassword,
      newPropertyType,
      image,
      showNotification,
      selectedFloor,
      storePass,
      callFetch,
    ]
  );

  return {
    isLoading,
    methods,
    selectedFloor,
    reset,
    handleSave,
    onUpdatePassword,
    isPassword,
    image,
    storePass,
  };
};

export default useSellerInformation;
